import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import Search from "assets/icons/search.gif";
import {
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridColDef,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRenderCellParams,
  GridRowGroupingModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import KeysContext from "contexts/KeysContext";
import StatusCell from "./Cells/StatusCell";
import tagsService from "api/tagsService";
import statusService from "api/statusService";
import TagCell from "./Cells/TagCell";
import IconsCell from "./Cells/IconsCell";
import MenuCell from "./Cells/MenuCell";
import CasesContext from "contexts/CasesContext";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import ShowCaseModal from "components/ShowCaseModal";
import { plGridLocales } from "utils/muiGridLocales";
import CustomToolbar from "components/CaseDossier/Toolbar";
import TitleCell from "./Cells/TitleCell";
import SignatureCell from "./Cells/SignatureCell";
import { nullHandlingSortComparator } from "utils/nullHandlingSortComparator";
import Spinner from "components/Spinner";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { ListType } from "dto/ListType";
import { EStatusGlobalType, StatusType } from "dto/IStatus";
import { TagType } from "dto/ITag";
import { IGridFilter } from "dto/IGridFilter";
import ClientCell from "./Cells/ClientCell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import useGridRowSelection from "hooks/useGridRowSelection";

const CourtPortalCasesRegistry: FC<Props> = ({
  onlyFromCourtPortal,
  searchText,
  setSubtitle,
}) => {
  const { t } = useTranslation();

  enum ArchivalFilter {
    All = 0,
    Current = 1,
    Archival = 2,
  }

  const CustomNoRowsOverlay = () => {
    return (
      <div className={styles.empty}>
        <img src={Search} />
        <div>{t("emptyStates.noResults")}</div>
      </div>
    );
  };

  const { cases: casesContext, setCases: setCasesContext } =
    useContext(CasesContext);
  const [isInitialized, setIsInitialized] = useState(false);
  const { keys } = useContext(KeysContext);

  const [prevPage, setPrevPage] = useState<number | null>(null);
  const [, setNextPage] = useState(0);
  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData } = statusService.useGetStatuses();

  const [filterStatusId, setFilterStatusId] = useState<number>(null);
  const [filterTagId, setFilterTagId] = useState<string>(null);
  const [filterUserId, setFilterUserId] = useState<string>(null);
  const [archivalFilter, setArchivalFilter] = useState<ArchivalFilter>(
    ArchivalFilter.All
  );

  const statuses = useMemo(
    () => statusesData?.data ?? [],
    [statusesData?.data]
  );
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);
  const rowsMemo = useMemo(() => casesContext ?? [], [casesContext]);

  const getStatus = (item: ICaseSimple) => {
    if (!item.StatusId) return null;

    const s = statuses.find((x) => x.Id == item.StatusId);

    if (s) {
      return s.Name;
    }

    return null;
  };

  const getFirstTag = (item: ICaseSimple) => {
    if (!item.TagIds || item.TagIds.length === 0) return null;

    const tag = tags.find((x) => x.Id == item.TagIds[0]);

    if (tag) {
      return tag.Name;
    }

    return null;
  };

  const columns: GridColDef[] = [
    {
      field: "CourtPortal",
      width: 110,
      groupable: true,
      headerName: t("columns.appeal"),
      valueGetter: (value, row) =>
        row.CourtPortal
          ? row.CourtPortal.replace("Apelacja ", "").replace("API Testowe ", "")
          : null,
      groupingValueGetter: (value, row) =>
        row.CourtPortal
          ? row.CourtPortal.replace("Apelacja ", "").replace("API Testowe ", "")
          : null,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.appeal")}</div>;
      },
    },

    {
      field: "CourtShortcut",
      minWidth: 140,
      headerName: t("columns.court"),
      cellClassName: `${styles.nameCell}`,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.court")}</div>;
      },
      valueGetter: (value, row) => row?.CourtShortcut,
      groupingValueGetter: (value, row) => row?.Court ?? t("registry.noCourt"),
      sortComparator: nullHandlingSortComparator,
    },

    {
      field: "Department",
      minWidth: 170,
      headerName: t("columns.department"),
      cellClassName: `${styles.nameCell}`,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.department")}</div>;
      },
      valueGetter: (value, row) => row?.CourtPortalCase?.Department,
      groupingValueGetter: (value, row) => row?.Court ?? t("registry.noDepartment"),
      sortComparator: nullHandlingSortComparator,
    },

    {
      field: "JudgeName",
      minWidth: 140,
      headerName: t("columns.judge"),
      cellClassName: `${styles.nameCell}`,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.judge")}</div>;
      },
      valueGetter: (value, row) => row?.CourtPortalCase?.JudgeName,
      groupingValueGetter: (value, row) => row?.Court ?? t("registry.noJudge"),
      sortComparator: nullHandlingSortComparator,
    },

    {
      field: "Signature",
      minWidth: 120,
      headerName: t("columns.signature"),
      cellClassName: `${styles.nameCell}`,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.signatureShort")}</div>;
      },
      valueGetter: (value, row) => row?.Signature,
      groupingValueGetter: (value, row) => row?.Signature ?? t("registry.noSignature"),
      sortComparator: nullHandlingSortComparator,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) => (
        <SignatureCell element={params.row}></SignatureCell>
      ),
    },

    {
      field: "Status",

      width: 140,
      headerName: t("columns.status"),
      headerAlign: "center",
      align: "center",
      cellClassName: `${styles.nameCell}`,
      type: "string",
      valueGetter: (value, row) => getStatus(row),
      groupingValueGetter: (value, row) => getStatus(row) ?? t("registry.noStatus"),
      sortComparator: nullHandlingSortComparator,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) =>
        params.rowNode.type !== "group" ? (
          <StatusCell element={params.row} statuses={statuses}></StatusCell>
        ) : (
          <div>{params.value}</div>
        ),
    },

    {
      field: "Client",
      minWidth: 120,
      headerName: t("columns.client"),
      cellClassName: `${styles.nameCell}`,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.client")}</div>;
      },
      valueGetter: (value, row) => row?.Client,
      groupingValueGetter: (value, row) => row?.Client ?? t("registry.noClient"),
      sortComparator: nullHandlingSortComparator,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) =>
        params.rowNode.type !== "group" ? (
          <ClientCell element={params.row}></ClientCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "Title",
      minWidth: 200,
      flex: 1,
      headerName: t("columns.title"),
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.Title,
      groupingValueGetter: (value, row) => row?.Title ?? t("registry.noTitle"),
      sortComparator: nullHandlingSortComparator,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) => (
        <TitleCell element={params.row}></TitleCell>
      ),
    },

    {
      field: "TagIds",
      width: 140,
      headerName: t("columns.label"),
      headerAlign: "center",
      align: "left",
      cellClassName: `${styles.nameCell}`,
      type: "string",
      valueGetter: (value, row) => getFirstTag(row),

      groupingValueGetter: (value, row) => getFirstTag(row) ?? t("registry.noLabel"),
      sortComparator: nullHandlingSortComparator,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) =>
        params.rowNode.type !== "group" ? (
          <TagCell element={params.row} tags={tags} apiRef={apiRef}></TagCell>
        ) : (
          <div>{params.value}</div>
        ),
    },

    {
      field: "Result",
      width: 130,
      groupable: true,
      headerName: t("columns.resolution"),
      groupingValueGetter: (value, row) => row.Result,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.resolution")}</div>;
      },
    },

    {
      field: "Value",
      width: 130,
      groupable: true,
      headerName: t("columns.disputeValue"),
      groupingValueGetter: (value, row) => row?.CourtPortalCase?.Value,
      valueGetter: (value, row) => row?.CourtPortalCase?.Value,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.valueShort")}</div>;
      },
    },

    {
      field: "CreationTimestamp",
      width: 100,
      headerName: t("columns.creationDate"),
      groupable: false,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.createdShort")}</div>;
      },
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("DD.MM.yyyy");
        }
        return null;
      },
    },

    {
      field: "LastModified",
      width: 120,
      headerName: t("columns.updatedDate"),
      groupable: false,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.updatedShort")}</div>;
      },
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("yyyy.DD.MM");
        }
        return null;
      },
    },

    {
      field: "FinishDate",
      width: 100,
      headerName: t("columns.finishedDate"),
      groupable: false,
      renderHeader: () => {
        return <div className={styles.header}>{t("columns.finishedShort")}</div>;
      },
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("yyyy.DD.MM");
        }
        return null;
      },
    },
    // {
    //   field: "Counter",
    //   headerName: "Licznik",
    //   type: "number",
    //   valueGetter: (value, row) => 1,
    //   renderHeader: (r) => {
    //     return <div className={styles.header}></div>;
    //   },
    //   sortComparator: nullHandlingSortComparator,
    // },
    {
      field: "Icons",
      width: 130,
      headerName: "Ikony",
      renderHeader: () => <div></div>,
      disableReorder: true,
      sortable: false,
      resizable: false,
      hideable: false,
      groupable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) =>
        params.rowNode.type !== "group" ? (
          <IconsCell
            element={params.row}
            statuses={statuses}
            keys={keys}
          ></IconsCell>
        ) : (
          <></>
        ),
    },
    {
      field: "Menu",
      width: 40,
      headerName: "Menu",
      renderHeader: () => <div></div>,
      disableReorder: true,
      sortable: false,
      resizable: false,
      hideable: false,
      groupable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<ICaseSimple, string>) =>
        params.rowNode.type !== "group" ? (
          <MenuCell element={params.row} keys={keys}></MenuCell>
        ) : (
          <></>
        ),
    },
  ];

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>([]);

  const columnsMemo = useMemo(() => columns, [tags, statuses]);

  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [selectedCase] = useState<ICaseSimple>(null);
  const handleRowDoubleClick = (itm: ICaseSimple) => {
    // setSelectedCase(itm);
    // setShowPreview(true);

    window.open(
      `${
        import.meta.env.VITE_APP_SITE ?? "https://alfa.pocztaprawnicza.pl"
      }/cases/${itm.Id}/court_portal?fullscreen=1`
    );
  };

  const {
    data,
    fetchNextPage,
    isLoading,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ["casesRegistry", searchText, filterStatusId, filterTagId, filterUserId],
    async ({ pageParam = 0 }) => {
      const query = new URLSearchParams(window.location.search);
      if (searchText.length) {
        query.append("Text", searchText);
      }

      query.append("Page", pageParam);
      query.append("IncludeRemoved", "true");

      query.append("SortProp", "Date");
      query.append("SortOrder", "Descending");

      if (filterStatusId) {
        query.append("StatusId", filterStatusId.toString());
      }

      if (filterTagId) {
        query.append("TagId", filterTagId);
      }

      if (filterUserId) {
        query.append("LeadBy", filterUserId);
      }

      if (archivalFilter === ArchivalFilter.Archival) {
        query.append(
          "WithGlobalStatus",
          EStatusGlobalType.Case_Archival.toString()
        );
      }

      if (archivalFilter === ArchivalFilter.Current) {
        query.append(
          "WithoutGlobalStatus",
          EStatusGlobalType.Case_Archival.toString()
        );
      }

      if (onlyFromCourtPortal) {
        query.append("FromCourtPortal", "true");
      }

      // if (forId) {
      //   query.append("PageForId", forId);
      // }

      // if (onlyDesk) {
      //   query.append("Desk", "true");
      // }

      const res = await axiosInstance.get(`/cases?${query}`);

      return {
        items: res.data,
        page: res.data.Page,
        LastPage: res.data.LastPage,
        nextPage: res.data.Page + 1,
      };
    },

    {
      staleTime: 0,
      cacheTime: 0,
      enabled: false,

      getPreviousPageParam: (firstPage) => {
        if (firstPage?.page > 0) {
          return firstPage.page - 1;
        }
        return undefined;
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.page + 1;
        }
        return undefined;
      },
    }
  );

  useEffect(() => {
    setNextPage(0);
    setTimeout(() => {
      refetch();
    }, 100);
  }, [searchText, filterStatusId, filterTagId, filterUserId, archivalFilter]);

  const compileData = async () => {
    if (!data) return;

    setNextPage(data.pages[data.pages.length - 1].nextPage);
    if (!prevPage) {
      setPrevPage(data.pages[data.pages.length - 1].page - 1);
    }

    if (prevPage && data.pages[data.pages.length - 1].page <= prevPage) {
      setPrevPage(data.pages[data.pages.length - 1].page - 1);
    }
    const packs = await Promise.all(
      data?.pages
        .sort((a, b) => {
          return a.page - b.page;
        })
        .map((page) => {
          return page.items.Items;
        })
        .flat()
    );

    setCasesContext(packs);
    setIsInitialized(true);
    // setKeys([...keys, OBKeys, NKeys].flat());
  };

  const apiRef = useGridApiRef();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();
  rowsRef.current = rowsMemo;

  const saveSnapshotInternal = () => {
    const currentState = apiRef.current.exportState();
    localStorage.setItem(
      "grid_courtCaseRegister",
      JSON.stringify(currentState)
    );
  };

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      saveSnapshotInternal();
    }
  }, [apiRef]);

  useEffect(() => {
    compileData();
  }, [data]);

  const [initialState, setInitialState] = useState<GridInitialState>(null);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem(
      "grid_courtCaseRegister"
    );
    const data: GridInitialStatePremium = stateFromLocalStorage
      ? JSON.parse(stateFromLocalStorage)
      : ({} as GridInitialStatePremium);
    data.pinnedColumns = { right: ["Menu"] };
    setInitialState(data);

    setColumnVisibilityModel(
      () => data?.columns?.columnVisibilityModel ?? {}
    );
    setGroupingModel(data.rowGrouping?.model ?? []);

    // handle refresh and navigating away/refreshing
    window.addEventListener("beforeunload", saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener("beforeunload", saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const hiddenFields = [
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    "Icons",
    "Menu",
  ];

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };

  const [editFilter, setEditFilter] = useState<IGridFilter>(null);
  const [showTypeModal, setShowTypeModal] = useState<boolean>(false);

  const getArchivalFilterLabel = (f: ArchivalFilter) => {
    switch (f) {
      case ArchivalFilter.All:
        return t("common.all");
      case ArchivalFilter.Current:
        return t("registry.active");
      case ArchivalFilter.Archival:
        return t("registry.archival");
    }
  };

  useEffect(() => {
    setSubtitle(getArchivalFilterLabel(archivalFilter));
  }, [archivalFilter]);

  const CustomToolbar2 = () => {
    return (
      <CustomToolbar
        listName="caseRegistry"
        editFilter={editFilter}
        setEditFilter={setEditFilter}
        listType={ListType.Cases}
        statusId={filterStatusId}
        setStatusId={setFilterStatusId}
        tagId={filterTagId}
        setTagId={setFilterTagId}
        statusType={StatusType.CASES}
        tagType={TagType.Cases}
        userId={filterUserId}
        setUserId={setFilterUserId}
        dateFilterLabel={t("registry.dateFilterLabel")}
        rightChildren={
          <div className={styles.type}>
            <div
              onClick={() => setShowTypeModal(!showTypeModal)}
              className={styles.typeText}
            >
              {getArchivalFilterLabel(archivalFilter)}
              <FontAwesomeIcon
                icon={showTypeModal ? faChevronUp : faChevronDown}
              />
            </div>
            {showTypeModal && (
              <div className={styles.dropdown}>
                <div
                  onClick={() => {
                    setArchivalFilter(ArchivalFilter.All);
                    setShowTypeModal(false);
                  }}
                  className={styles.box}
                >
                  {getArchivalFilterLabel(ArchivalFilter.All)}
                </div>
                <div
                  onClick={() => {
                    setArchivalFilter(ArchivalFilter.Current);
                    setShowTypeModal(false);
                  }}
                  className={styles.box}
                >
                  {getArchivalFilterLabel(ArchivalFilter.Current)}
                </div>
                <div
                  onClick={() => {
                    setArchivalFilter(ArchivalFilter.Archival);
                    setShowTypeModal(false);
                  }}
                  className={styles.box}
                >
                  {getArchivalFilterLabel(ArchivalFilter.Archival)}
                </div>
              </div>
            )}
          </div>
        }
      ></CustomToolbar>
    );
  };

  if (
    !initialState ||
    !statusesData?.data ||
    !tagsData?.data ||
    !tags ||
    !statuses
  ) {
    return <Spinner />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.list}>
        {/* {packages.length === 0 && !isLoading && isInitialized && (
          <div className={styles.empty}>
            <img src={Search} />
            <div>Nie znaleziono wyników dla wybranych kryteriów</div>
          </div>
        )} */}

        <DataGridPremium
          apiRef={apiRef}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
          initialState={initialState}
          localeText={plGridLocales}
          slots={{
            toolbar: CustomToolbar2,
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          showToolbar
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            columnsManagement: {
              getTogglableColumns,
            },
          }}
          loading={!isInitialized || isLoading}
          columnHeaderHeight={30}
          onRowsScrollEnd={() => {
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          onRowDoubleClick={(r) => {
            const itm = r.row as ICaseSimple;
            if (itm) {
              handleRowDoubleClick(itm);
            }
          }}
          columns={columnsMemo}
          hideFooter={true}
          getRowId={(r) => r.Id}
          rows={rowsMemo}
          disableColumnFilter={true}
          disableAggregation={true}
          disablePivoting={true}
          onColumnVisibilityModelChange={(m) => {
            console.log("ON Column visibility model changed", m);
            setColumnVisibilityModel(m);
            saveSnapshotInternal();
          }}
          onRowGroupingModelChange={(m) => {
            const newVisibility = { ...columnVisibilityModel };

            for (let i = 0; i < groupingModel.length; i++) {
              newVisibility[groupingModel[i]] = true;
            }

            for (let i = 0; i < m.length; i++) {
              newVisibility[m[i]] = false;
            }
            setColumnVisibilityModel(newVisibility);
            setGroupingModel(m);
          }}
          columnVisibilityModel={columnVisibilityModel}
        />
      </div>

      {showPreview && (
        <ShowCaseModal
          previewCase={selectedCase}
          toggle={() => setShowPreview(false)}
        ></ShowCaseModal>
      )}
    </div>
  );
};

export default CourtPortalCasesRegistry;
